import type { ReactNode } from 'react';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { LoadMoreSentinel } from '../../shared/load_more_sentinel.js';
import {
  ResultsOfflineRequest,
  type ResultsOfflineRequestProps,
} from '../../shared/results_page/results_offline_request.js';
import {
  ResultsFilteredEmpty,
  type ResultsFilteredEmptyCopy,
} from '../../shared/results_page/results_filtered_empty.js';
import {
  ResultsNotice,
  type ResultsDecisionNotice,
} from '../../shared/results_page/results_notice.js';
import type { RentalOfferView } from '../models.js';
import { RentalOfferCard, type RentalOfferCardCopy } from './rental_offer_card.js';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import './rental_results_list.css';

const PENDING_ROWS = 3;

/** As linhas fantasma da espera, na caixa do card real para a página cair onde
 *  já há forma. `aria-hidden` porque quem não vê já ouviu a espera no
 *  `role="status"` do cabeçalho. */
function RentalPendingRows(): ReactNode {
  return (
    <>
      {Array.from({ length: PENDING_ROWS }, (_, slot) => (
        <li key={slot} aria-hidden="true" className="hui-rental-results-pending">
          <div className="hui-rental-offer-card" data-layout="wide">
            <div className="hui-rental-offer-main">
              <div className="hui-rental-offer-media">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-[54px] w-[72px] rounded-md" />
              </div>
              <div className="hui-rental-offer-copy">
                <div className="hui-rental-offer-head">
                  <div className="grid min-w-0 flex-1 gap-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3.5 w-48 max-w-full" />
                  </div>
                  <div className="grid justify-items-end gap-1">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-3.5 w-20" />
                  </div>
                </div>
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          </div>
        </li>
      ))}
    </>
  );
}

export type RentalResultsListProps = {
  offers: readonly RentalOfferView[];
  /** O nome acessível da lista ("Carros encontrados"). */
  resultsLabel: string;
  cardCopy: RentalOfferCardCopy;
  selectedOfferId?: string;
  /** Uma escrita em voo: todos os `Selecionar` travam. */
  busy?: boolean;
  notice?: ResultsDecisionNotice;
  /** A lista completa ainda vem? Então as linhas em cena não são o fim dela. */
  pending?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMoreLabel?: string;
  onLoadMore?: () => void;
  /** A porta do pedido à equipe VOLL: só depois da última página, quando a
   *  lista já não promete mais nada (no meio da rolagem ela empurraria a
   *  sentinela). */
  offlineRequest?: ResultsOfflineRequestProps;
  /** A copy do aviso de corte, só com filtro em pé: a lista vazia fora de
   *  carga é o corte que escondeu todos os carros (#6268), e o aviso com
   *  `Limpar filtros` toma o lugar dos cards. Sem corte, a lista vazia é a da
   *  busca, e o desfecho dela é do host. */
  filteredEmpty?: ResultsFilteredEmptyCopy;
  onClearFilters?: () => void;
  onSelect(offerId: string): void;
};

/** Os cards em cena e, com a lista completa a caminho, as linhas fantasma — ou,
 *  com o corte em pé e nada a mostrar na lista esgotada, o aviso dele. Travada, a
 *  lista avisa mas não oferece o que não pode fazer. */
function RentalResultsRows({
  offers,
  resultsLabel,
  cardCopy,
  selectedOfferId,
  busy,
  pending,
  hasMore,
  loadingMore,
  filteredEmpty,
  onClearFilters,
  onSelect,
}: Readonly<
  Pick<
    RentalResultsListProps,
    | 'offers'
    | 'resultsLabel'
    | 'cardCopy'
    | 'selectedOfferId'
    | 'filteredEmpty'
    | 'onClearFilters'
    | 'onSelect'
  > & { busy: boolean; pending: boolean; hasMore: boolean; loadingMore: boolean }
>): ReactNode {
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  // Outra página ainda pode trazer um carro que passa: só a lista ESGOTADA
  // prova que o corte escondeu tudo.
  if (filteredEmpty && offers.length === 0 && !pending && !hasMore && !loadingMore) {
    return (
      <ResultsFilteredEmpty copy={filteredEmpty} onClear={busy ? undefined : onClearFilters} />
    );
  }
  // O desenho do card sai do PALCO, como no hotel (`NARROW_STAGE_QUERY`): o
  // card do celular só no palco estreito; no desktop, o de desktop em toda
  // largura, inclusive na coluna de 448 ao lado do mapa.
  const layout = narrow ? 'stack' : 'wide';
  return (
    <ul className="hui-rental-results-list" aria-label={resultsLabel}>
      {offers.map((offer) => (
        <li key={offer.id}>
          <RentalOfferCard
            offer={offer}
            copy={cardCopy}
            selected={selectedOfferId === offer.id}
            busy={busy}
            layout={layout}
            onSelect={onSelect}
          />
        </li>
      ))}
      {pending && <RentalPendingRows />}
    </ul>
  );
}

/**
 * O corpo da lista de carros: o aviso de estado, os cards e a sentinela do fim
 * — as três peças respondem à MESMA pergunta, o que a lista mostra agora.
 */
export function RentalResultsList({
  offers,
  resultsLabel,
  cardCopy,
  selectedOfferId,
  busy = false,
  notice,
  pending = false,
  hasMore = false,
  loadingMore = false,
  loadMoreLabel,
  onLoadMore,
  offlineRequest,
  filteredEmpty,
  onClearFilters,
  onSelect,
}: Readonly<RentalResultsListProps>): ReactNode {
  return (
    <>
      {notice && <ResultsNotice notice={notice} />}
      <RentalResultsRows
        offers={offers}
        resultsLabel={resultsLabel}
        cardCopy={cardCopy}
        selectedOfferId={selectedOfferId}
        busy={busy}
        pending={pending}
        hasMore={hasMore}
        loadingMore={loadingMore}
        filteredEmpty={filteredEmpty}
        onClearFilters={onClearFilters}
        onSelect={onSelect}
      />
      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loadingMore}
        {...(loadMoreLabel ? { label: loadMoreLabel } : {})}
        {...(onLoadMore ? { onLoadMore } : {})}
      />
      {offlineRequest && !pending && !hasMore && !loadingMore && (
        <div className="mt-4">
          <ResultsOfflineRequest {...offlineRequest} />
        </div>
      )}
    </>
  );
}
